// SMPTE timecode helpers (F-02.6, F-02.12, D-24).

/** Nominal integer rate used for timecode counting (23.976 → 24, 29.97 → 30). */
export function nominalRate(fps: number): number {
  return Math.max(1, Math.round(fps));
}

/** Drop-frame is only defined for 29.97 and 59.94 (nominal 30 / 60). */
export function supportsDropFrame(fps: number): boolean {
  const n = nominalRate(fps);
  return (n === 30 || n === 60) && Math.abs(fps - n) > 0.001;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/** Converts a frame count to HH:MM:SS:FF (or HH:MM:SS;FF for drop-frame). */
export function framesToTimecode(frames: number, fps: number, dropFrame = false): string {
  const rate = nominalRate(fps);
  const neg = frames < 0;
  let f = Math.abs(Math.floor(frames));
  const df = dropFrame && (rate === 30 || rate === 60);
  if (df) {
    const dropPerMin = rate === 30 ? 2 : 4;
    const framesPer10Min = rate * 600 - dropPerMin * 9;
    const framesPerMin = rate * 60 - dropPerMin;
    const d = Math.floor(f / framesPer10Min);
    const m = f % framesPer10Min;
    f += dropPerMin * 9 * d + (m > dropPerMin ? dropPerMin * Math.floor((m - dropPerMin) / framesPerMin) : 0);
  }
  const ff = f % rate;
  const totalSec = Math.floor(f / rate);
  const ss = totalSec % 60;
  const mm = Math.floor(totalSec / 60) % 60;
  const hh = Math.floor(totalSec / 3600) % 24;
  const sep = df ? ';' : ':';
  return `${neg ? '-' : ''}${pad2(hh)}:${pad2(mm)}:${pad2(ss)}${sep}${pad2(ff)}`;
}

export interface ParsedTimecode {
  hh: number;
  mm: number;
  ss: number;
  ff: number;
  dropFrame: boolean;
}

export function parseTimecode(text: string): ParsedTimecode | null {
  const m = /^\s*(\d{1,2})[:;.](\d{1,2})[:;.](\d{1,2})([:;.])(\d{1,3})\s*$/.exec(text);
  if (!m) return null;
  return { hh: +m[1], mm: +m[2], ss: +m[3], ff: +m[5], dropFrame: m[4] === ';' || text.includes(';') };
}

/** Converts a timecode to a frame count. Returns null for malformed input. */
export function timecodeToFrames(text: string, fps: number, dropFrameHint?: boolean): number | null {
  const tc = parseTimecode(text);
  if (!tc) return null;
  const rate = nominalRate(fps);
  if (tc.ff >= rate || tc.ss >= 60 || tc.mm >= 60) return null;
  const df = (dropFrameHint ?? tc.dropFrame) && (rate === 30 || rate === 60);
  let frames = ((tc.hh * 60 + tc.mm) * 60 + tc.ss) * rate + tc.ff;
  if (df) {
    const dropPerMin = rate === 30 ? 2 : 4;
    const totalMinutes = tc.hh * 60 + tc.mm;
    frames -= dropPerMin * (totalMinutes - Math.floor(totalMinutes / 10));
  }
  return frames;
}

/**
 * Parses user "go to" input: a plain frame number (display base applied) or a timecode.
 * Returns a 0-based frame index relative to the displayed timecode origin.
 */
export function parseGoto(
  text: string,
  fps: number,
  base: 0 | 1,
  startTimecode?: { text: string; dropFrame: boolean },
): number | null {
  const t = text.trim();
  if (/^-?\d+$/.test(t)) return parseInt(t, 10) - base;
  const f = timecodeToFrames(t, fps, startTimecode?.dropFrame);
  if (f === null) return null;
  if (startTimecode) {
    const start = timecodeToFrames(startTimecode.text, fps, startTimecode.dropFrame);
    if (start !== null) return f - start;
  }
  return f;
}

/** Display timecode for an A frame index under the chosen mode (D-24). */
export function displayTimecode(
  frame: number,
  fps: number,
  mode: 'embedded' | 'from-zero',
  startTimecode?: { text: string; dropFrame: boolean },
): string {
  if (mode === 'embedded' && startTimecode) {
    const start = timecodeToFrames(startTimecode.text, fps, startTimecode.dropFrame) ?? 0;
    return framesToTimecode(start + frame, fps, startTimecode.dropFrame);
  }
  return framesToTimecode(frame, fps, false);
}
